import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import MainLayout from '@/components/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft, 
  Clock, 
  Video, 
  MapPin, 
  CreditCard,
  Calendar,
  Save,
  Trash2,
  Sparkles,
  ExternalLink,
  Copy,
  Check,
  Upload,
  Mic,
  Image,
  X,
  Loader2,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const SessionDetailPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [notes, setNotes] = useState('');
  const [homework, setHomework] = useState('');
  const [soapNotes, setSoapNotes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatingSoap, setGeneratingSoap] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [extractingImage, setExtractingImage] = useState(false);
  const [imageNotes, setImageNotes] = useState([]);
  const imageInputRef = useRef(null);
  
  // Image extraction preview state (for user confirmation before save)
  const [extractionPreview, setExtractionPreview] = useState(null);

  useEffect(() => {
    fetchSession();
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const response = await fetch(`${API}/sessions/${sessionId}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSession(data);
        setNotes(data.notes || '');
        setHomework(data.homework || '');
        setSoapNotes(data.soap_notes);
        setImageNotes(data.image_notes || []);
      } else {
        toast.error('Session not found');
        navigate('/sessions');
      }
    } catch (error) {
      toast.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const handleExtractImageNotes = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Unsupported image format. Please use JPG, PNG, or WebP.');
      return;
    }

    // Check file size (10MB limit)
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 10) {
      toast.error(`Image too large (${fileSizeMB.toFixed(1)}MB). Maximum size is 10MB.`);
      return;
    }

    setExtractingImage(true);
    setExtractionPreview(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/sessions/${sessionId}/extract-image-notes`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeaders()['Authorization']
        },
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to extract text from image');
      }
      
      const data = await response.json();
      
      // Add to image notes list
      setImageNotes(prev => [...prev, { 
        image_id: data.image_id, 
        filename: data.filename || file.name, 
        extracted_text: data.extracted_text,
        extraction_success: data.extraction_success,
        uploaded_at: new Date().toISOString() 
      }]);
      
      // Handle extraction result
      if (data.extraction_success) {
        // Show preview for user confirmation - do NOT auto-save
        setExtractionPreview({
          imageId: data.image_id,
          filename: data.filename || file.name,
          extractedText: data.extracted_text,
          suggestedAppend: data.suggested_append
        });
        toast.success('Text extracted! Review and confirm to add to notes.');
      } else {
        // Extraction failed - show clear error message
        toast.error(data.extraction_error || 'Image extraction failed. Please review manually.', {
          duration: 5000
        });
      }
      
    } catch (error) {
      toast.error(error.message || 'Failed to extract text from image');
    } finally {
      setExtractingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  // Confirm and append extracted text to notes
  const handleConfirmExtraction = () => {
    if (!extractionPreview) return;
    
    // Append extracted text to notes
    const newNotes = notes + extractionPreview.suggestedAppend;
    setNotes(newNotes);
    
    toast.success('Extracted text added to notes. Remember to save!');
    setExtractionPreview(null);
  };

  // Dismiss extraction preview without adding to notes
  const handleDismissExtraction = () => {
    setExtractionPreview(null);
    toast.info('Extraction dismissed. Image saved but text not added to notes.');
  };

  const handleDeleteImageNote = async (imageId) => {
    try {
      const response = await fetch(`${API}/sessions/${sessionId}/image-notes/${imageId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete');
      
      setImageNotes(prev => prev.filter(img => img.image_id !== imageId));
      toast.success('Image note deleted');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/sessions/${sessionId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ notes, homework, soap_notes: soapNotes })
      });

      if (!response.ok) throw new Error('Failed to save');
      
      const updated = await response.json();
      setSession(updated);
      toast.success('Notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSoap = async () => {
    if (!notes.trim()) {
      toast.error('Please add session notes first');
      return;
    }

    setGeneratingSoap(true);
    try {
      const response = await fetch(`${API}/sessions/${sessionId}/generate-soap`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to generate SOAP notes');
      
      const data = await response.json();
      setSoapNotes(data.soap_notes);
      toast.success('SOAP notes generated');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGeneratingSoap(false);
    }
  };

  const handleMarkPaid = async () => {
    try {
      const response = await fetch(`${API}/sessions/${sessionId}/mark-paid`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to mark as paid');
      
      const updated = await response.json();
      setSession(updated);
      toast.success('Session marked as paid! Calendar invite sent.');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSendPaymentReminder = async () => {
    setSendingReminder(true);
    try {
      const response = await fetch(`${API}/sessions/${sessionId}/send-payment-reminder`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reminder');
      }
      
      toast.success(data.message || 'Payment reminder sent!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingReminder(false);
    }
  };

  const handleCopyPaymentLink = () => {
    if (session.payment_link) {
      navigator.clipboard.writeText(session.payment_link);
      setCopiedLink(true);
      toast.success('Payment link copied!');
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const handleTranscribeAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/m4a', 'audio/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Unsupported audio format. Please use MP3, WAV, M4A, or WebM.');
      return;
    }

    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/sessions/${sessionId}/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeaders()['Authorization']
        },
        credentials: 'include',
        body: formData
      });

      if (!response.ok) throw new Error('Transcription failed');
      
      const data = await response.json();
      setNotes(data.notes);
      toast.success('Audio transcribed successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setTranscribing(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success('Session deleted');
      navigate('/sessions');
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-primary">Loading session...</div>
        </div>
      </MainLayout>
    );
  }

  if (!session) return null;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="session-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/sessions')}
            className="rounded-xl"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 
              className="text-2xl sm:text-3xl font-semibold text-foreground"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Session with {session.patient_name}
            </h1>
            <div className="flex items-center gap-2 text-muted-foreground mt-1">
              <Calendar className="h-4 w-4" />
              {format(new Date(session.scheduled_date), 'EEEE, MMMM d, yyyy')}
              <span className="mx-1">•</span>
              <Clock className="h-4 w-4" />
              {session.scheduled_time}
            </div>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="rounded-xl text-destructive hover:bg-destructive/10">
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Session</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this session? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-destructive">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Session Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-muted">
                  {session.mode === 'video' ? (
                    <Video className="h-5 w-5 text-primary" />
                  ) : (
                    <MapPin className="h-5 w-5 text-primary" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Mode</p>
                  <p className="font-medium text-foreground capitalize">{session.mode}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-muted">
                  <CreditCard className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fee</p>
                  <p className="font-medium text-foreground">
                    {session.fee ? `₹${session.fee}` : 'Not set'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge 
                    variant="outline"
                    className={`mt-1 ${session.status === 'confirmed' ? 'badge-confirmed' : 'badge-tentative'}`}
                  >
                    {session.status}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm text-muted-foreground">Payment</p>
                  <Badge 
                    variant="outline"
                    className={`mt-1 ${session.payment_status === 'paid' ? 'badge-paid' : 'badge-pending'}`}
                  >
                    {session.payment_status}
                  </Badge>
                </div>
                {session.payment_status === 'pending' && (
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      className="rounded-lg bg-primary hover:bg-primary/90"
                      onClick={handleMarkPaid}
                      data-testid="mark-paid-btn"
                    >
                      Mark Paid
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg text-muted-foreground"
                      onClick={handleSendPaymentReminder}
                      disabled={sendingReminder}
                      data-testid="send-reminder-btn"
                    >
                      {sendingReminder ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <Send className="h-4 w-4 mr-1" />
                      )}
                      Remind
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payment & Meet Links */}
        {(session.payment_link || session.meet_link) && (
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                {session.payment_link && session.payment_status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Payment Link:</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg"
                      onClick={handleCopyPaymentLink}
                      data-testid="copy-payment-link-btn"
                    >
                      {copiedLink ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                      {copiedLink ? 'Copied!' : 'Copy UPI Link'}
                    </Button>
                  </div>
                )}
                {session.meet_link && session.payment_status === 'paid' && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Meet Link:</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg"
                      onClick={() => window.open(session.meet_link, '_blank')}
                      data-testid="meet-link-btn"
                    >
                      <Video className="h-4 w-4 mr-1" />
                      Join Meeting
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes Tabs */}
        <Tabs defaultValue="notes" className="space-y-4">
          <TabsList className="bg-muted rounded-xl p-1">
            <TabsTrigger value="notes" className="rounded-lg">Session Notes</TabsTrigger>
            <TabsTrigger value="soap" className="rounded-lg">SOAP Notes</TabsTrigger>
            <TabsTrigger value="homework" className="rounded-lg">Homework</TabsTrigger>
          </TabsList>

          <TabsContent value="notes" className="space-y-4">
            <Card className="border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg" style={{ fontFamily: 'Fraunces, serif' }}>
                  Session Notes
                </CardTitle>
                <div className="flex gap-2">
                  {/* Image Upload for Text Extraction */}
                  <input
                    type="file"
                    ref={imageInputRef}
                    accept="image/*"
                    className="hidden"
                    onChange={handleExtractImageNotes}
                    disabled={extractingImage}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={extractingImage}
                    data-testid="upload-image-btn"
                  >
                    {extractingImage ? (
                      <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Extracting...</>
                    ) : (
                      <><Image className="h-4 w-4 mr-1" />Upload Image</>
                    )}
                  </Button>
                  
                  {/* Audio Upload */}
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      onChange={handleTranscribeAudio}
                      disabled={transcribing}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg"
                      disabled={transcribing}
                      asChild
                    >
                      <span>
                        {transcribing ? (
                          <>Transcribing...</>
                        ) : (
                          <>
                            <Mic className="h-4 w-4 mr-1" />
                            Upload Audio
                          </>
                        )}
                      </span>
                    </Button>
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    onClick={handleSaveNotes}
                    disabled={saving}
                    data-testid="save-notes-btn"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Extraction Preview - User must confirm before adding to notes */}
                {extractionPreview && (
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 space-y-3" data-testid="extraction-preview">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Image className="h-5 w-5 text-amber-600" />
                        <span className="font-medium text-amber-900">
                          Extracted from: {extractionPreview.filename}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={handleDismissExtraction}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="bg-white rounded-lg p-3 border border-amber-100 max-h-[200px] overflow-y-auto">
                      <p className="text-sm text-foreground whitespace-pre-wrap">
                        {extractionPreview.extractedText}
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-amber-700">
                        Review the extracted text above. Click "Add to Notes" to append, or dismiss to discard.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-lg"
                          onClick={handleDismissExtraction}
                        >
                          Dismiss
                        </Button>
                        <Button
                          size="sm"
                          className="rounded-lg bg-amber-600 hover:bg-amber-700"
                          onClick={handleConfirmExtraction}
                          data-testid="confirm-extraction-btn"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Add to Notes
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add your session notes here..."
                  className="min-h-[200px] rounded-xl"
                  data-testid="session-notes-textarea"
                />
                
                {/* Image Notes Section */}
                {imageNotes.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Uploaded Images</Label>
                    <div className="space-y-2">
                      {imageNotes.map((img) => (
                        <div key={img.image_id} className="p-3 rounded-xl bg-muted/50 relative group">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Image className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">{img.filename}</span>
                                {img.extraction_success === false && (
                                  <Badge variant="destructive" className="text-xs">
                                    Extraction Failed
                                  </Badge>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(img.uploaded_at), 'MMM d, yyyy h:mm a')}
                                </span>
                              </div>
                              {img.extracted_text ? (
                                <p className="text-sm text-muted-foreground line-clamp-3">{img.extracted_text}</p>
                              ) : (
                                <p className="text-sm text-muted-foreground italic">No text extracted</p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleDeleteImageNote(img.image_id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="soap" className="space-y-4">
            <Card className="border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg" style={{ fontFamily: 'Fraunces, serif' }}>
                  SOAP Notes
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg"
                  onClick={handleGenerateSoap}
                  disabled={generatingSoap || !notes.trim()}
                  data-testid="generate-soap-btn"
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  {generatingSoap ? 'Generating...' : 'Generate with AI'}
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {soapNotes ? (
                  <>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Subjective</Label>
                      <Textarea
                        value={soapNotes.subjective || ''}
                        onChange={(e) => setSoapNotes(prev => ({ ...prev, subjective: e.target.value }))}
                        className="rounded-xl"
                        placeholder="Patient's reported symptoms, feelings, concerns..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Objective</Label>
                      <Textarea
                        value={soapNotes.objective || ''}
                        onChange={(e) => setSoapNotes(prev => ({ ...prev, objective: e.target.value }))}
                        className="rounded-xl"
                        placeholder="Observable behaviors, affect, appearance..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Assessment</Label>
                      <Textarea
                        value={soapNotes.assessment || ''}
                        onChange={(e) => setSoapNotes(prev => ({ ...prev, assessment: e.target.value }))}
                        className="rounded-xl"
                        placeholder="Clinical impressions, progress..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Plan</Label>
                      <Textarea
                        value={soapNotes.plan || ''}
                        onChange={(e) => setSoapNotes(prev => ({ ...prev, plan: e.target.value }))}
                        className="rounded-xl"
                        placeholder="Treatment plan, homework, next steps..."
                      />
                    </div>
                    <Button
                      onClick={handleSaveNotes}
                      disabled={saving}
                      className="rounded-xl bg-primary hover:bg-primary/90"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save SOAP Notes'}
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No SOAP notes yet</p>
                    <p className="text-sm mt-1">Add session notes first, then generate SOAP notes with AI</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="homework" className="space-y-4">
            <Card className="border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg" style={{ fontFamily: 'Fraunces, serif' }}>
                  Homework & Tasks
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg"
                  onClick={handleSaveNotes}
                  disabled={saving}
                >
                  <Save className="h-4 w-4 mr-1" />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={homework}
                  onChange={(e) => setHomework(e.target.value)}
                  placeholder="Add homework, exercises, or tasks for the patient..."
                  className="min-h-[200px] rounded-xl"
                  data-testid="homework-textarea"
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default SessionDetailPage;
